import { bindable, autoinject } from 'aurelia-framework';
import { DOM } from 'aurelia-pal';
import { I18N } from 'aurelia-i18n';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ServiceResponse } from '../../../../models/service-response';
import { CreateApplePayWebSessionRequest } from '../../../../models/hpfApi';
import {
	ApplePayLineItemBuilder,
	ApplePayPaymentMethodUpdateBuilder,
	ApplePayPaymentRequestBuilder,
	ApplePaySettings,
	ApplePayUtil,
	DigitalWalletType
} from '../../../../models/digital-wallet';
import { HpfDigitalWalletService } from '../../../../services/hpf-digital-wallet.service';
import { HpfFeeService } from '../../../../services/hpf-fee.service';
import { ApplePayValidator } from './apple-pay-validator';
import { MerchantConsumerPortalFeeSettings } from '../../../../models/merchant-consumer-portal-fee-settings';
import { PaymentPlanSettings } from '../../../../models/payment-plan-settings';
import { PayrazrService } from '../../../../services/payrazr';
import { PaymentPlanType } from '../../../../models/paymentPlanType';
import { CalculateFeeRequestBuilder } from '../../../../models/hpfApi/calculate-fee-request-builder';
import { PaymentMethodType } from '../../../../services/hpf-transaction-service';

@autoinject
export class ApplePay {
	@bindable hasCorrectSignature: boolean;
	@bindable hasAcceptedTerms: boolean;
	@bindable applePaySettings: ApplePaySettings;
	@bindable merchantKey: number;
	@bindable customerAccountKeys: number[];
	@bindable merchantConsumerPortalFeeSettings: MerchantConsumerPortalFeeSettings;
	@bindable content: string;
	@bindable hashedContent: string;
	@bindable paymentPlanSettings: PaymentPlanSettings;

	applePayPaymentRequest: ApplePayJSV14.ApplePayPaymentRequest;
	session: ApplePaySession;
	calculatedServiceFee: number;

	get isPaymentPlan(): boolean {
		return this.paymentPlanSettings?.isPaymentPlan() ?? false;
	}

	get isInstallmentPlan(): boolean {
		return this.isPaymentPlan && this.paymentPlanSettings?.paymentPlanType === PaymentPlanType.Installment;
	}

	get isRecurringPlan(): boolean {
		return this.isPaymentPlan && this.paymentPlanSettings?.paymentPlanType === PaymentPlanType.Recurring;
	}

	get isSingleFuture(): boolean {
		return this.applePaySettings.paymentTiming === 'deferred';
	}

	get feesEnabled(): boolean {
		return this.isPaymentPlan
			? this.merchantConsumerPortalFeeSettings.feesEnabled && this.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans
			: this.merchantConsumerPortalFeeSettings.feesEnabled;
	}

	constructor(
		private element: Element,
		private hpfDigitalWalletService: HpfDigitalWalletService,
		private hpfFeeService: HpfFeeService,
		private payrazrService: PayrazrService,
		private eventAggregator: EventAggregator,
		private applePayValidator: ApplePayValidator,
		private i18n: I18N) { }

	async attached() {
		this.applePaySettings.version = await this.hpfDigitalWalletService.getDigitalWalletServiceSettings()
			.then(response => response.isSuccessful ? Number(response.value.applePayJsVersion) : 0)
			.catch(() => 0);
	}

	canMakeApplePayPayments(): boolean {
		if (window
			&& window.ApplePaySession
			&& window.ApplePaySession.canMakePayments()
			&& this.applePaySettings
			&& this.applePaySettings.hasValidVersion()
			&& window.ApplePaySession.supportsVersion(this.applePaySettings.version)) {
			return true;
		}
		return false;
	}

	onApplePayButtonClicked() {
		if (!this.canMakeApplePayPayments()) {
			this.publishApplePayValidationErrors([this.i18n.i18next.t('applePayFailed', 'Failed to setup Apple Pay, please contact customer support')]);
			return;
		}

		this.applePayPaymentRequest = this.getApplePayPaymentRequest();

		const signatureAndTermsValidationErrors: string[] = this.applePayValidator.validateSignatureAndTerms(this.hasCorrectSignature, this.hasAcceptedTerms);
		const requestValidationErrors: string[] = this.applePayValidator.validatePaymentRequest(this.applePayPaymentRequest);
		const validationErrors: string[] = [...signatureAndTermsValidationErrors, ...requestValidationErrors];

		// do not start session with invalid request
		if (validationErrors.length > 0) {
			this.publishApplePayValidationErrors(validationErrors);
			return;
		}

		this.session = this.getApplePaySession(this.applePayPaymentRequest);

		this.setApplePaySessionEventHandlers();

		// open apple pay sheet
		this.session.begin();
	}

	setApplePaySessionEventHandlers(): void {
		// Validate merchant
		this.session.onvalidatemerchant = (validateMerchantEvent: ApplePayJS.ApplePayValidateMerchantEvent) => {
			const createApplePayWebSessionRequest: CreateApplePayWebSessionRequest = {
				merchantKey: this.merchantKey
			};

			this.hpfDigitalWalletService.createApplePayWebSession(createApplePayWebSessionRequest)
				.then((response: ServiceResponse) => {
					if (response.isSuccessful) {
						const paySession = JSON.parse(
							atob(response.value.replace(/_/g, '/'))
						);
						this.session.completeMerchantValidation(paySession);
					}
					else {
						this.publishApplePayValidationErrors([this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support')]);
						this.session.abort();
					}
				})
				.catch((error: any) => {
					this.publishApplePayValidationErrors([this.i18n.i18next.t('applePaySessionFailed', 'Failed to create Apple Pay Session, please contact customer support')]);
					this.session.abort();
				});
		};

		// Payment method changed
		this.session.onpaymentmethodselected = (paymentMethodSelectedEvent: ApplePayJS.ApplePayPaymentMethodSelectedEvent): void => {
			console.log('payment method selected', paymentMethodSelectedEvent);

			// billingContactName is redacted from paymentMethodSelectedEvent, so only validate card type and zip code
			const paymentMethodTypeValidationErrors = this.applePayValidator.validatePaymentMethodType(paymentMethodSelectedEvent?.paymentMethod?.type);
			const postalCodeValidationErrors = this.applePayValidator.validatePostalCode(paymentMethodSelectedEvent?.paymentMethod?.billingContact?.postalCode);
			const validationErrors = [...paymentMethodTypeValidationErrors, ...postalCodeValidationErrors];

			/**
			 * Bug: (need to test further) ApplePayJS onPaymentMethodSelected is called twice when changing payment methods.
			 * Note: I think this is b/c requiredBillingContactFields is included in ApplePayPaymentRequest
			 * Solution: To prevent a confusing ux, do not include validation errors in update obj
			 */
			if (validationErrors.length > 0) {
				const amount: string = this.isPaymentPlan ? '0' : this.applePaySettings.totalAmount;
				const newTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
					.setLabel()
					.setAmount(amount)
					.setType(this.applePaySettings.totalType)
					.getApplePayLineItem();

				const update: ApplePayJSV14.ApplePayPaymentMethodUpdate = new ApplePayPaymentMethodUpdateBuilder()
					.setTotal(newTotal)
					.getApplePayPaymentMethodUpdate();

				this.session.completePaymentMethodSelection(update);
				return;
			}

			if (this.isInstallmentPlan) {
				this.handleInstallmentPlanNewPaymentMethodSelected(paymentMethodSelectedEvent);
			}
			else if (this.isRecurringPlan) {
				this.handleRecurringPlanNewPaymentMethodSelected(paymentMethodSelectedEvent);
			}
			else {
				this.handleSinglePayNewPaymentMethodSelected(paymentMethodSelectedEvent);
			}
		};

		// Submit payment
		this.session.onpaymentauthorized = (paymentAuthorizedEvent: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
			console.log('payment authorized', paymentAuthorizedEvent);

			const billingContactValidationErrors = this.applePayValidator.validateBillingContact(paymentAuthorizedEvent?.payment?.billingContact);
			if (billingContactValidationErrors.length > 0) {
				this.session.completePayment({ status: ApplePaySession.STATUS_FAILURE, errors: billingContactValidationErrors });
				return;
			}

			this.dispatchCustomEvent('confirmation-submit', {
				content: this.content,
				hashedContent: this.hashedContent,
				payment: paymentAuthorizedEvent.payment,
				calculatedServiceFee: this.calculatedServiceFee,
				digitalWalletType: DigitalWalletType.ApplePay,
				digitalWalletSession: this.session
			});
		};

		// Cancel session
		this.session.oncancel = (event: ApplePayJS.Event) => { };
	}

	getApplePayPaymentRequest(): ApplePayJSV14.ApplePayPaymentRequest {
		const total: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
			.setLabel()
			.setAmount(this.applePaySettings.totalAmount)
			.setType('pending')
			.getApplePayLineItem();

		const applePayPaymentRequestBuilder: ApplePayPaymentRequestBuilder = new ApplePayPaymentRequestBuilder(this.applePaySettings)
			.setTotal(total)
			.setCountryCode()
			.setCurrencyCode()
			.setMerchantCapabilites()
			.setSupportedNetworks()
			.setRequiredBillingContactFields();

		if (this.isPaymentPlan) {
			applePayPaymentRequestBuilder.setRecurringPaymentRequest(this.applePaySettings.recurringAmount);
		}

		return applePayPaymentRequestBuilder.getApplePayPaymentRequest();
	}

	getApplePaySession(request: ApplePayJSV14.ApplePayPaymentRequest): ApplePaySession {
		return new ApplePaySession(this.applePaySettings.version, request);
	}

	handleInstallmentPlanNewPaymentMethodSelected(paymentMethodSelectedEvent: ApplePayJS.ApplePayPaymentMethodSelectedEvent): void {
		if (!this.feesEnabled) {
			const paymentPlanSubtotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel('Installment Subtotal')
				.setAmount(this.paymentPlanSettings.installment.totalAmount)
				.getApplePayLineItem();

			const paymentPlanFeeTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel('Total Conveinence Fees')
				.setAmount('0')
				.getApplePayLineItem();

			const paymentPlanTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel('Installment Total')
				.setAmount(this.paymentPlanSettings.installment.totalAmount)
				.getApplePayLineItem();

			const newLineItems: ApplePayJSV14.ApplePayLineItem[] = [paymentPlanSubtotal, paymentPlanFeeTotal, paymentPlanTotal];

			const newTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel()
				.setAmount('0')
				.setType('pending')
				.getApplePayLineItem();

			const update: ApplePayJSV14.ApplePayPaymentMethodUpdate = new ApplePayPaymentMethodUpdateBuilder()
				.setTotal(newTotal)
				.setNewLineItems(newLineItems)
				.setRecurringPaymentRequest(this.applePayPaymentRequest.recurringPaymentRequest)
				.getApplePayPaymentMethodUpdate();

			this.session.completePaymentMethodSelection(update);
			return;
		}

		this.payrazrService.computePlannedPayments(
			this.paymentPlanSettings.accountId,
			this.paymentPlanSettings.installment.totalAmount,
			this.paymentPlanSettings.installment.number,
			this.paymentPlanSettings.installment.nextPaymentDate,
			this.paymentPlanSettings.installment.payToday,
			this.paymentPlanSettings.installment.selectedFrequency,
			paymentMethodSelectedEvent.paymentMethod?.billingContact?.postalCode,
			ApplePayUtil.getPaymentMethodType(paymentMethodSelectedEvent.paymentMethod),
			null)
			.then((response: any) => {
				if (!response || !response.plannedPayments) {
					throw new Error(this.i18n.i18next.t('paymentPlansComputeError', 'There was an error computing this contract'));
				}

				const firstPlannedPayment = response.plannedPayments[0];
				const firstPlannedPaymentServiceFee = firstPlannedPayment.planedServiceFee;
				const firstPaymentTotal = firstPlannedPayment.billAmount + firstPlannedPaymentServiceFee;
				const newRecurringAmount = firstPaymentTotal?.toString() ?? '0';

				const planSubtotal: string = this.paymentPlanSettings.installment.totalAmount;
				const totalConveinenceFees: number = response.plannedPayments?.reduce((sum, plannedPayment) => sum + plannedPayment.planedServiceFee, 0) ?? 0;
				const planTotal: number = (Number(this.paymentPlanSettings.installment.totalAmount) + totalConveinenceFees);

				const paymentPlanSubtotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
					.setLabel('Installment Subtotal')
					.setAmount(planSubtotal)
					.getApplePayLineItem();

				const paymentPlanFeeTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
					.setLabel('Total Conveinence Fees')
					.setAmount(totalConveinenceFees.toString())
					.getApplePayLineItem();

				const paymentPlanTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
					.setLabel('Installment Total')
					.setAmount(planTotal.toString())
					.getApplePayLineItem();

				const newLineItems: ApplePayJSV14.ApplePayLineItem[] = [paymentPlanSubtotal, paymentPlanFeeTotal, paymentPlanTotal];

				const newRecurringPaymentRequest: ApplePayJSV14.ApplePayRecurringPaymentRequest = new ApplePayPaymentRequestBuilder(this.applePaySettings)
					.setRecurringPaymentRequest(newRecurringAmount)
					.getApplePayPaymentRequest()
					.recurringPaymentRequest;

				// setup-installments.ts uses calculatedServiceFee to set singleServiceFee
				this.calculatedServiceFee = firstPlannedPaymentServiceFee;

				const newTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
					.setLabel()
					.setAmount('0')
					.setType('pending')
					.getApplePayLineItem();

				const update: ApplePayJSV14.ApplePayPaymentMethodUpdate = new ApplePayPaymentMethodUpdateBuilder()
					.setTotal(newTotal)
					.setNewLineItems(newLineItems)
					.setRecurringPaymentRequest(newRecurringPaymentRequest)
					.getApplePayPaymentMethodUpdate();

				this.session.completePaymentMethodSelection(update);
			})
			.catch((error: any) => {
				this.publishApplePayValidationErrors([this.i18n.i18next.t('paymentPlansComputeError', 'There was an error computing this contract')]);
				this.session.abort();
			});
	}

	handleRecurringPlanNewPaymentMethodSelected(paymentMethodSelectedEvent: ApplePayJS.ApplePayPaymentMethodSelectedEvent): void {
		if (!this.feesEnabled) {
			const recurringPlanPaymentAmount: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel('Recurring Payment Amount')
				.setAmount(this.paymentPlanSettings.recurring.paymentAmount)
				.getApplePayLineItem();

			const recurringPlanFirstPaymentFee: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel('Conveinence Fee for First Payment')
				.setAmount('0')
				.getApplePayLineItem();

			const recurringSubsequentPaymentFee: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel('Conveinence Fee for Subsequent Payments')
				.setAmount('0')
				.getApplePayLineItem();

			const newLineItems: ApplePayJSV14.ApplePayLineItem[] = [recurringPlanPaymentAmount, recurringPlanFirstPaymentFee, recurringSubsequentPaymentFee];

			const newTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel()
				.setAmount('0')
				.setType('pending')
				.getApplePayLineItem();

			const update: ApplePayJSV14.ApplePayPaymentMethodUpdate = new ApplePayPaymentMethodUpdateBuilder()
				.setTotal(newTotal)
				.setNewLineItems(newLineItems)
				.setRecurringPaymentRequest(this.applePayPaymentRequest.recurringPaymentRequest)
				.getApplePayPaymentMethodUpdate();

			this.session.completePaymentMethodSelection(update);
			return;
		}

		const amount: number = parseInt(this.applePaySettings.recurringAmount, 10);
		const paymentMethodType: PaymentMethodType = ApplePayUtil.getPaymentMethodType(paymentMethodSelectedEvent.paymentMethod);

		const calculateFeeRequest = new CalculateFeeRequestBuilder()
			.setUserType('C')
			.setMerchantKeys([this.merchantKey])
			.setZipCode(paymentMethodSelectedEvent.paymentMethod.billingContact?.postalCode)
			.setPaymentMethodType(paymentMethodType)
			.setAmountUsedToCalculateSurchargeOnIfApplicable(amount)
			.setCustomerAccountKeys(this.customerAccountKeys)
			.setPaymentFeeType(this.paymentPlanSettings?.paymentFeeType)
			.getCalculateFeeRequest();

		this.hpfFeeService.calculateFee(calculateFeeRequest)
			.then((response: ServiceResponse) => {
				if (response.isSuccessful) {
					this.calculatedServiceFee = response.value.convenienceFee;
					const newRecurringAmount = (Number(this.applePaySettings.recurringAmount) + this.calculatedServiceFee).toString();

					const recurringPlanPaymentAmount: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
						.setLabel('Recurring Payment Amount')
						.setAmount(this.applePaySettings.recurringAmount)
						.getApplePayLineItem();

					const recurringPlanFirstPaymentFee: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
						.setLabel('Conveinence Fee for First Payment')
						.setAmount(this.calculatedServiceFee.toString())
						.getApplePayLineItem();

					const subsequentFee: number = this.merchantConsumerPortalFeeSettings.allowServiceFeesOnEveryTransaction ? this.calculatedServiceFee : 0;
					const recurringSubsequentPaymentFee: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
						.setLabel('Conveinence Fee for Subsequent Payments')
						.setAmount(subsequentFee.toString())
						.getApplePayLineItem();

					const newLineItems: ApplePayJSV14.ApplePayLineItem[] = [recurringPlanPaymentAmount, recurringPlanFirstPaymentFee, recurringSubsequentPaymentFee];

					const newRecurringPaymentRequest: ApplePayJSV14.ApplePayRecurringPaymentRequest = new ApplePayPaymentRequestBuilder(this.applePaySettings)
						.setRecurringPaymentRequest(newRecurringAmount)
						.getApplePayPaymentRequest()
						.recurringPaymentRequest;

					const newTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
						.setLabel()
						.setAmount('0')
						.setType('pending')
						.getApplePayLineItem();

					const update: ApplePayJSV14.ApplePayPaymentMethodUpdate = new ApplePayPaymentMethodUpdateBuilder()
						.setTotal(newTotal)
						.setNewLineItems(newLineItems)
						.setRecurringPaymentRequest(newRecurringPaymentRequest)
						.getApplePayPaymentMethodUpdate();

					this.session.completePaymentMethodSelection(update);
				}
				else {
					this.publishApplePayValidationErrors([this.i18n.i18next.t('applePayPaymentMethodSelectFailed', 'Failed to update Apple Pay payment method, please contact customer support')]);
					this.session.abort();
				}
			})
			.catch((error: any) => {
				this.publishApplePayValidationErrors([this.i18n.i18next.t('applePayPaymentMethodSelectFailed', 'Failed to update Apple Pay payment method, please contact customer support')]);
				this.session.abort();
			});
	}

	handleSinglePayNewPaymentMethodSelected(paymentMethodSelectedEvent: ApplePayJS.ApplePayPaymentMethodSelectedEvent): void {
		if (!this.feesEnabled) {
			let newTotalAmount = this.isSingleFuture ? '0' : this.applePaySettings.totalAmount;
			const paymentMethodUpdateBuilder = new ApplePayPaymentMethodUpdateBuilder();

			if (this.isSingleFuture) {
				// alike payment plans, single future needs to process a $0 auth transaction
				const deferredPayment: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
					.setLabel('Scheduled Payment')
					.setAmount(this.applePaySettings.totalAmount)
					.setPaymentTiming('deferred')
					.setDeferredPaymentDate(this.applePaySettings.deferredPaymentDate)
					.getApplePayLineItem();

				const newLineItems: ApplePayJSV14.ApplePayLineItem[] = [deferredPayment];

				paymentMethodUpdateBuilder.setNewLineItems(newLineItems);
			}

			const newTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
				.setLabel()
				.setAmount(newTotalAmount)
				.setType(this.applePaySettings.totalType)
				.getApplePayLineItem();

			const update: ApplePayJSV14.ApplePayPaymentMethodUpdate = paymentMethodUpdateBuilder
				.setTotal(newTotal)
				.getApplePayPaymentMethodUpdate();

			this.session.completePaymentMethodSelection(update);
			return;
		}

		const amount: number = parseInt(this.applePaySettings.totalAmount, 10);
		const paymentMethodType: PaymentMethodType = ApplePayUtil.getPaymentMethodType(paymentMethodSelectedEvent.paymentMethod);

		const calculateFeeRequest = new CalculateFeeRequestBuilder()
			.setUserType('C')
			.setMerchantKeys([this.merchantKey])
			.setZipCode(paymentMethodSelectedEvent.paymentMethod.billingContact?.postalCode)
			.setPaymentMethodType(paymentMethodType)
			.setAmountUsedToCalculateSurchargeOnIfApplicable(amount)
			.setCustomerAccountKeys(this.customerAccountKeys)
			.setPaymentFeeType(this.paymentPlanSettings?.paymentFeeType)
			.getCalculateFeeRequest();

		this.hpfFeeService.calculateFee(calculateFeeRequest)
			.then((response: ServiceResponse) => {
				if (response.isSuccessful) {
					this.calculatedServiceFee = response.value.convenienceFee;

					const calculatedTotal: number = Number(this.applePaySettings.totalAmount) + this.calculatedServiceFee;
					let newTotalAmount: string = this.isSingleFuture ? '0' : calculatedTotal.toString();

					const feeTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
						.setLabel('Fee')
						.setAmount(this.calculatedServiceFee.toString())
						.getApplePayLineItem();

					const subtotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
						.setLabel('Subtotal')
						.setAmount(this.applePaySettings.totalAmount)
						.getApplePayLineItem();

					const newLineItems: ApplePayJSV14.ApplePayLineItem[] = [subtotal, feeTotal];

					if (this.isSingleFuture) {
						// alike payment plans, single future needs to process a $0 auth transaction
						const deferredPayment: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
							.setLabel('Scheduled Payment')
							.setAmount(calculatedTotal.toString())
							.setPaymentTiming('deferred')
							.setDeferredPaymentDate(this.applePaySettings.deferredPaymentDate)
							.getApplePayLineItem();

						newLineItems.push(deferredPayment);
					}

					const newTotal: ApplePayJSV14.ApplePayLineItem = new ApplePayLineItemBuilder()
						.setLabel()
						.setAmount(newTotalAmount)
						.setType(this.applePaySettings.totalType)
						.getApplePayLineItem();

					const update: ApplePayJSV14.ApplePayPaymentMethodUpdate = new ApplePayPaymentMethodUpdateBuilder()
						.setTotal(newTotal)
						.setNewLineItems(newLineItems)
						.getApplePayPaymentMethodUpdate();

					this.session.completePaymentMethodSelection(update);
				}
				else {
					this.publishApplePayValidationErrors([this.i18n.i18next.t('applePayPaymentMethodSelectFailed', 'Failed to update Apple Pay payment method, please contact customer support')]);
					this.session.abort();
				}
			})
			.catch((error: any) => {
				this.publishApplePayValidationErrors([this.i18n.i18next.t('applePayPaymentMethodSelectFailed', 'Failed to update Apple Pay payment method, please contact customer support')]);
				this.session.abort();
			});
	}

	dispatchCustomEvent(eventName: any, eventData?: any) {
		let evt = DOM.createCustomEvent(eventName, { bubbles: true, cancelable: true, detail: eventData });
		this.element.dispatchEvent(evt);
	}

	publishApplePayValidationErrors(validationErrors: string[]): void {
		this.eventAggregator.publish('digitalWalletValidationErrorChange', validationErrors);
	}
}
